import React from 'react';
import { Project } from '../data/portfolio-data';
import { ArrowRight, Zap, Clock, TrendingUp, ChevronRight } from 'lucide-react';

interface ProjectCardProps {
  project: Project;
  onOpenDetails: (project: Project) => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onOpenDetails }) => {
  const metricsPriority = [
    'traffic growth',
    'load time improvement',
    'process reduction',
    'build time',
    'error rate',
    'data accuracy',
    'user adoption'
  ];
  
  // Find the strongest metric for the one-line business win
  const oneLineField = metricsPriority.find(field => project[field]);
  const oneLineWin = oneLineField
    ? `${(project[oneLineField] as string).replace('+', '')}`
    : '';

  // Get 2-3 high-impact metrics
  const statsFields = metricsPriority.filter(field => project[field]);
  const statsToShow = statsFields.slice(0, 3);

  // Shorten text for preview
  const shortChallenge = project.challenge.length > 150
    ? project.challenge.slice(0, 147) + '...'
    : project.challenge;

  const shortSolution = project.solution.length > 150
    ? project.solution.slice(0, 147) + '...'
    : project.solution;

  // Get image path
  let imagePath = '/images/placeholder.jpg';
  let websiteUrl = '';
  if (project.title === 'Shee & Fowl') websiteUrl = 'https://sheeandfowl.com/';
  else if (project.title === 'Bloom Elixirs') websiteUrl = 'https://bloomelixirs.com/';
  if (project.title === 'Bloom Elixirs') imagePath = '/images/bloom_preview.jpg';
  else if (project.title === 'Shee & Fowl') imagePath = '/images/sheeandfowl_preview.webp';
  else if (project.title.includes('VPS Automation')) imagePath = '/images/vps_preview.jpeg';

  const titleCase = (str: string) =>
    str
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

  // Get metric icon
  const getMetricIcon = (metric: string) => {
    if (metric.includes('time')) return <Clock className="w-4 h-4" />;
    if (metric.includes('traffic')) return <TrendingUp className="w-4 h-4" />;
    return <Zap className="w-4 h-4" />;
  };

  return (
    <div className="bg-blue-900/20 backdrop-blur-lg rounded-lg overflow-hidden shadow-lg shadow-blue-900/10 hover:shadow-blue-500/20 transition-all duration-300 hover:translate-y-[-2px]">
      <div className="flex flex-col lg:flex-row">
        {/* Left side - Image */}
        <div className="lg:w-[45%] relative">
          <img
            src={imagePath}
            alt={`${project.title} preview`}
            className="w-full h-full object-cover"
            style={{ minHeight: '400px' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent" />
          {websiteUrl && (
            <a
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute inset-0 z-10"
              aria-label={`Visit ${project.title} site`}
            />
          )}
        </div>

        {/* Right side - Content */}
        <div className="lg:w-[55%] p-8 lg:p-10 flex flex-col bg-gradient-to-r from-blue-900/40 to-blue-900/20">
          {/* Header */}
          <div className="mb-8">
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-2">{project.title}</h3>
            <p className="text-blue-300 text-sm tracking-wide">{project.clientIndustry} · {project.year}</p>
          </div>

          {/* Quick Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {statsToShow.map(field => (
              <div key={field} className="bg-blue-800/20 rounded-lg px-4 py-3">
                <div className="flex items-center gap-2 text-blue-200 mb-1.5">
                  {getMetricIcon(field)}
                  <span className="text-[11px] uppercase tracking-wider">{field.replace(/_/g, ' ')}</span>
                </div>
                <span className="text-lg font-semibold text-white">{project[field]}</span>
              </div>
            ))}
          </div>

          {/* Challenge, Solution, Results Summary */}
          <div className="space-y-6 mb-8">
            <div>
              <h4 className="text-blue-200 font-medium text-xs uppercase tracking-wider mb-2">Challenge</h4>
              <p className="text-blue-100 text-sm leading-relaxed line-clamp-2">{project.challenge}</p>
            </div>
            <div className="border-t border-blue-800/20 pt-6">
              <h4 className="text-blue-200 font-medium text-xs uppercase tracking-wider mb-2">Solution</h4>
              <p className="text-blue-100 text-sm leading-relaxed line-clamp-2">{project.solution}</p>
            </div>
            <div className="border-t border-blue-800/20 pt-6">
              <h4 className="text-blue-200 font-medium text-xs uppercase tracking-wider mb-2">Results</h4>
              <p className="text-blue-100 text-sm leading-relaxed line-clamp-2">{project.results}</p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-auto pt-6 border-t border-blue-800/20">
            {/* Tech Stack */}
            <div className="flex flex-wrap gap-2 mb-6">
              {project.techStack.slice(0, 4).map((tech) => (
                <span key={tech} className="px-3 py-1 bg-blue-800/10 rounded-full text-blue-200 text-xs">
                  {tech}
                </span>
              ))}
              {project.techStack.length > 4 && (
                <span className="px-2 py-1 text-blue-300 text-xs flex items-center">
                  +{project.techStack.length - 4} more
                  <ChevronRight className="w-3 h-3 ml-0.5" />
                </span>
              )}
            </div>

            {/* View Case Study Button */}
            <button
              onClick={() => onOpenDetails(project)}
              className="w-full px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-center group"
            >
              View Case Study
              <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard; 